import { Checkbox, Flex, Text, Link } from '@servicetitan/anvil2';
import {
    LoadingScreen,
    useLegalDisclosure,
    HtmlContent,
    SignatureCanvas,
    isBlank,
} from '@servicetitan/financing-utils-shared';
import { useState, useEffect, useMemo, Fragment } from 'react';
import { ApplicationDataStore } from '../application/ApplicationDataStore';
import './GreenSkyDisclosureText.scss';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';

interface GreenSkyDisclosureTextProps {
    onDisclosureChange?: (allAgreed: boolean) => void;
    onApplicantSignatureChange?: (base64Signature: string | null) => void;
    onCoApplicantSignatureChange?: (base64Signature: string | null) => void;
}

interface StateDisclosureEntry {
    state: string;
    value: string;
}

interface DisclosureItem {
    category: string;
    text: string;
}

const getStateDisclosureText = (rawJson: string, state: string): string => {
    if (!rawJson || !state) {
        return '';
    }
    try {
        const entries: StateDisclosureEntry[] = JSON.parse(rawJson);
        return entries.find(e => e.state.toLowerCase() === state.toLowerCase())?.value ?? '';
    } catch {
        return '';
    }
};

export const GreenSkyDisclosureText = ({
    onDisclosureChange,
    onApplicantSignatureChange,
    onCoApplicantSignatureChange,
}: GreenSkyDisclosureTextProps) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();

    const hasCoApplicant = !!applicationData?.coApplicant?.hasCoApplicant;

    const providerName = 'GreenSky';
    const tenantId = setupData?.tenantId;

    const [applicantSignature, setApplicantSignature] = useState<string | null>(null);
    const [coApplicantSignature, setCoApplicantSignature] = useState<string | null>(null);

    const submissionText = useLegalDisclosure({
        providerName,
        tenantId,
        category: 'submission',
    }) as string;
    const submitButtonText = useLegalDisclosure({
        providerName,
        tenantId,
        category: 'submitButton',
    }) as string;
    const submissionCoAppText = useLegalDisclosure({
        providerName,
        tenantId,
        category: 'submissionWithCoApplicant',
    }) as string;
    const submitButtonCoAppText = useLegalDisclosure({
        providerName,
        tenantId,
        category: 'submitButtonWithCoApplicant',
    }) as string;

    const appStateDisclosureRaw = useLegalDisclosure({
        providerName,
        tenantId,
        category: 'stateSubmission',
    }) as string;

    const coAppStateDisclosureRaw = useLegalDisclosure({
        providerName,
        tenantId,
        category: hasCoApplicant ? 'stateSubmissionCoApplicant' : undefined,
    }) as string;

    const jobDetailsState =
        applicationData?.applicant?.primaryApplicant?.jobDetails?.address?.state;
    const applicantState = !isBlank(jobDetailsState)
        ? jobDetailsState
        : (applicationData?.mailingAddress?.primaryApplicant?.address?.state ?? '');

    const coApplicantInner = (applicationData?.coApplicant as any)?.coApplicant;
    const coApplicantState = coApplicantInner?.isAddressSameAsPrimary
        ? applicantState
        : (coApplicantInner?.address?.state ?? applicantState);

    const appStateText = getStateDisclosureText(appStateDisclosureRaw, applicantState);
    const coAppStateText = hasCoApplicant
        ? getStateDisclosureText(coAppStateDisclosureRaw, coApplicantState)
        : '';

    const disclosureItems = useMemo<DisclosureItem[]>(
        () =>
            [
                { category: 'submission', text: submissionText },
                { category: 'submitButton', text: submitButtonText },
                ...(hasCoApplicant
                    ? [
                          { category: 'submissionWithCoApplicant', text: submissionCoAppText },
                          { category: 'submitButtonWithCoApplicant', text: submitButtonCoAppText },
                      ]
                    : []),
            ].filter(item => item.text !== ''),
        [
            hasCoApplicant,
            submissionText,
            submitButtonText,
            submissionCoAppText,
            submitButtonCoAppText,
        ],
    );

    const [checkboxes, setCheckboxes] = useState<{ [category: string]: boolean }>({});

    const handleChange = (category: string) => {
        setCheckboxes(prev => ({ ...prev, [category]: !prev[category] }));
    };

    const handleApplicantSignatureChange = (base64Signature: string | null) => {
        setApplicantSignature(base64Signature);
        onApplicantSignatureChange?.(base64Signature);
    };

    const handleCoApplicantSignatureChange = (base64Signature: string | null) => {
        setCoApplicantSignature(base64Signature);
        onCoApplicantSignatureChange?.(base64Signature);
    };

    useEffect(() => {
        const greenSkyAttested = disclosureItems.every(item => checkboxes[item.category] === true);

        const applicantSigned = !!applicantSignature;
        const coApplicantPresent = !!applicationData?.coApplicant?.hasCoApplicant;
        const coApplicantSigned = coApplicantPresent ? !!coApplicantSignature : true;

        const allAttested = greenSkyAttested && applicantSigned && coApplicantSigned;

        onDisclosureChange?.(allAttested);
    }, [
        checkboxes,
        applicantSignature,
        coApplicantSignature,
        onDisclosureChange,
        applicationData,
        disclosureItems,
    ]);

    if (!disclosureItems.length) {
        return <LoadingScreen />;
    }

    return (
        <Flex direction="column" gap="4">
            <Text variant="body">
                Please read the following important agreements and disclosures. This is a credit
                application, and submitting it means that you have read and agree to the steps
                described below. These agreements and disclosures can be found online anytime at{' '}
                <Link target="_blank" rel="noreferrer" href="https://www.greensky.com/disclosures">
                    www.greensky.com/disclosures
                </Link>
                . Please retain a copy of them for your records.
            </Text>
            <Text variant="body">
                Definitions: For the purposes of these disclosures, the following definitions apply:
            </Text>
            <Text variant="body">
                "Lender," "us," "we" or "our" shall include any financial institution, and their
                agents, service providers and representatives, participating in the Greensky Loan
                Program (the Greensky Program).
            </Text>
            <Text variant="body">
                1. APPLICATION AGREEMENTS AND DISCLOSURES FOR GREENSKY CONSUMER LOAN PROGRAM
            </Text>
            <Text variant="body">
                We are providing the following disclosures to you in connection with your
                application for a loan from one of the financial institutions offering GreenSky®
                Program.
            </Text>
            {disclosureItems.map(item => (
                <Fragment key={item.category}>
                    <Checkbox
                        label={
                            <>
                                <HtmlContent content={item.text} />
                                {item.category === 'submission' && appStateText && (
                                    <HtmlContent content={appStateText} />
                                )}
                                {item.category === 'submissionWithCoApplicant' &&
                                    coAppStateText && <HtmlContent content={coAppStateText} />}
                            </>
                        }
                        onChange={() => handleChange(item.category)}
                        checked={!!checkboxes[item.category]}
                    />
                    {item.category === 'submitButton' && (
                        <SignatureCanvas
                            onSignatureChange={handleApplicantSignatureChange}
                            label="Sign here"
                            clearButtonText="Clear"
                        />
                    )}
                </Fragment>
            ))}
            {hasCoApplicant && (
                <SignatureCanvas
                    onSignatureChange={handleCoApplicantSignatureChange}
                    label="Sign here"
                    clearButtonText="Clear"
                />
            )}
        </Flex>
    );
};
